import { useEffect, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { selectThemeMode } from "./themeSlice";

export type ResolvedTheme = "light" | "dark";

const systemPrefersDark = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

/**
 * The theme actually in effect — the chosen mode, or the live system preference
 * when the mode is "auto".
 *
 * Everything styled with Tailwind reads the `.dark` class instead; this is for
 * the few consumers that need a concrete value in JS (the toast container and
 * other third-party widgets that take a theme prop). Deriving it from state
 * rather than observing the DOM means it is correct on the very first render,
 * which a MutationObserver — firing only on later changes — never is.
 */
export const useResolvedTheme = (): ResolvedTheme => {
  const mode = useAppSelector(selectThemeMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (mode !== "auto" || typeof window.matchMedia !== "function") return;

    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => setSystemDark(query.matches);
    // Re-sync on entering auto: the preference may have changed while pinned.
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [mode]);

  if (mode === "auto") {
    return systemDark ? "dark" : "light";
  }
  return mode;
};
